import { useMemo, useSyncExternalStore } from 'react';
import { Dimensions } from 'react-native';

import {
  createMatchByBreakpointIndex,
  getBreakpointsByMinWidth,
  getCurrentBreakpoint,
  getHeightConstraints,
  getScreenOrientation,
} from './utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns different utilities and dynamic values to manage component responsiveness.
 *
 * Re-renders only when the width breakpoint, the height constraint or the
 * orientation changes, not on every window resize. Use `useWindowDimensions`
 * when the exact window size is needed.
 *
 * @example
 *
 * const { minWidth, currentBreakpoint, orientation, match } = useResponsive();
 *
 * const shouldShowBtn = minWidth.SM;
 * const shouldCollapseNavbar = currentBreakpoint.isXS;
 * const shouldShowVideoInFullscreen = orientation.isLandscape;
 * const responsiveStyles = match([styles.containerXS, styles.containerSM, ...]);
 */
export const useResponsive = () => {
  const responsiveKey = useSyncExternalStore(
    subscribeToWindowChanges,
    getResponsiveKey,
    getResponsiveKey,
  );

  return useMemo(() => createResponsiveHelpers(responsiveKey), [responsiveKey]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function subscribeToWindowChanges(onWindowChange: () => void) {
  const subscription = Dimensions.addEventListener('change', onWindowChange);

  return () => {
    subscription.remove();
  };
}

function getResponsiveKey() {
  const { width, height } = Dimensions.get('window');

  return JSON.stringify([
    getBreakpointsByMinWidth(width),
    getHeightConstraints(height),
    getScreenOrientation(width, height),
  ]);
}

/**
 * Rebuilds the helpers from the key alone, so they change exactly when the key does.
 */
function createResponsiveHelpers(responsiveKey: string) {
  const [minWidth, currentHeight, orientation] = JSON.parse(responsiveKey) as ResponsiveKeyParts;
  const currentBreakpoint = getCurrentBreakpoint(minWidth);
  const match = createMatchByBreakpointIndex(currentBreakpoint);

  return { minWidth, currentBreakpoint, currentHeight, orientation, match };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ResponsiveKeyParts = [
  ReturnType<typeof getBreakpointsByMinWidth>,
  ReturnType<typeof getHeightConstraints>,
  ReturnType<typeof getScreenOrientation>,
];
